import DeleteIcon from '@mui/icons-material/Delete';
import { Box, DialogContent, Grid, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  addJobBoardAccount,
  deleteByEntity,
  getJobBoardLogins,
  getJobBoardSessionSettings,
  IConfiguredJobBoardDTO,
  IJobBoardLoginDTO,
} from 'wisestep-api';
import {
  Label,
  WsButton,
  WsColors,
  WsDataTable,
  WsDialog,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';

import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';

import JobBoardLoginForm from './JobBoardLoginForm';

const JobBoardLogins = () => {
  const [indexNo, setIndexNo] = useState(-1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [availableJobBoards, setAvailableJobBoards] = useState<WsSelectObj[]>();
  const [configuredJobBoards, setConfiguredJobBoards] = useState<IConfiguredJobBoardDTO[]>([]);
  const [jobBoardName, setJobBoardName] = useState('choose');
  const jbUrl = 'api/prism-jobboard-logins';
  useEffect(() => {
    (async function () {
      const tempArray: WsSelectObj[] = [{ label: 'choose...', value: 'choose' }];

      const getJobBoardSessionSettingsData = await getJobBoardSessionSettings();
      if (getJobBoardSessionSettingsData.data) {
        getJobBoardSessionSettingsData.data.map((list) =>
          tempArray.push({ label: list.jobBoard, value: list.jobBoard })
        );
        setAvailableJobBoards(tempArray);
      }
    })();
  }, []);

  const formDefValues = {
    selectJobBoards: jobBoardName,
  };
  const formOptions = {
    defaultValues: formDefValues,
  };
  const methods = useForm(formOptions);
  const { watch } = methods;

  const selectedJobBoard = watch('selectJobBoards');
  useEffect(() => {
    if (selectedJobBoard) {
      apiCallHandlerFetchJobBoards(selectedJobBoard);
    }
  }, [selectedJobBoard]);

  const apiCallHandlerFetchJobBoards = async (jbName: string) => {
    if (jbName === 'choose') {
      setJobBoardName(jbName);
      const getJobBoardLoginsData = await getJobBoardLogins(jbUrl);
      if (getJobBoardLoginsData.data) setConfiguredJobBoards(getJobBoardLoginsData.data);
    } else {
      setJobBoardName(jbName);
      const getJobBoardLoginsData = await getJobBoardLogins(`${jbUrl}/${jbName}`);
      if (!isEmpty(getJobBoardLoginsData.data)) setConfiguredJobBoards(getJobBoardLoginsData.data);
      else setConfiguredJobBoards([]);
    }
  };
  const addAccountHandler = async (data: FieldValues) => {
    const payload: IJobBoardLoginDTO = {
      jobBoard: jobBoardName,
      loginId: data.jobBoardUserName,
      password: data.jobBoardPassword,
    };
    try {
      const tempArray: IConfiguredJobBoardDTO[] = configuredJobBoards;
      const addJobBoardAccountData = await addJobBoardAccount(payload);
      tempArray.push(addJobBoardAccountData.data);

      setConfiguredJobBoards([...tempArray]);
      setNotification(success('Added Successfully.'));
    } catch (error) {
      setNotification(warning('Something went wrong, Please try again.'));
    }
  };
  const deleteHandler = async (index: number) => {
    if (configuredJobBoards) {
      try {
        const tempData: IConfiguredJobBoardDTO[] = configuredJobBoards;
        await deleteByEntity(configuredJobBoards[index]);
        setNotification(success('Deleted Successfully.'));
        tempData.splice(index, 1);
        setConfiguredJobBoards([...tempData]);
        setConfirmDelete(false);
        setIndexNo(-1);
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
  };
  const columns = [
    {
      Header: 'Job Board',
      accessor: 'jobBoard',
    },
    {
      Header: 'Login ID',
      accessor: 'loginID',
    },
    {
      Header: 'Password',
      accessor: 'password',
    },
    {
      Header: '',
      id: 'actions',
      Cell: (cell: Cell) => {
        return (
          <Tooltip title={'Delete'}>
            <DeleteIcon
              onClick={() => {
                setIndexNo(cell.row.index);
                setConfirmDelete(true);
              }}
              sx={{ fill: WsColors.Red500, cursor: 'pointer' }}
            />
          </Tooltip>
        );
      },
    },
  ];
  const tableData = useMemo(
    () =>
      configuredJobBoards?.map((list) => ({
        jobBoard: list.jobBoard,
        loginID: list.loginId,
        password: list.password,
      })),
    [configuredJobBoards]
  );
  return (
    <Box marginBottom={3}>
      <FormProvider {...methods}>
        <Grid container direction="column">
          <WsSelect
            variant="outlined"
            name="selectJobBoards"
            label="Select Job Board"
            selectArray={availableJobBoards ?? []}
          />
        </Grid>
        {jobBoardName != 'choose' && (
          <JobBoardLoginForm
            jobBoardName={jobBoardName}
            saveHandler={(data) => addAccountHandler(data)}
          />
        )}
        <Grid marginTop={2} container>
          <WsDataTable columns={columns} data={tableData ?? []} />
        </Grid>
      </FormProvider>
      {confirmDelete && (
        <WsDialog openDialog={true} size="small">
          <DialogContent>
            <Grid alignItems="center" container spacing={1} direction="column">
              <Grid item xs={12}>
                <Label sx={{ fontWeight: 'bold' }}>
                  Are you sure you want to delete this JobBoard Login?
                </Label>
              </Grid>
              <Grid
                item
                width="40%"
                display="flex"
                justifyContent="space-between"
                alignSelf="center"
              >
                <WsButton onClick={() => deleteHandler(indexNo)}>Yes</WsButton>
                <WsButton
                  onClick={() => {
                    setIndexNo(-1);
                    setConfirmDelete(false);
                  }}
                >
                  No
                </WsButton>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      )}
    </Box>
  );
};
export default JobBoardLogins;
